import { badgeTitle } from "./badges";
import { NORWAY, VIEWBOX } from "./norway";

// Untouched fylker sit off the ramp entirely. Interpolating from a pale green
// would make 0/28 and 1/28 the same colour to the eye, and the first club in a
// county is the most significant step the card can show.
export const UNPLAYED = "#eceff1";

const RAMP_FROM = [200, 230, 201]; // #C8E6C9
const RAMP_TO = [46, 125, 50]; // #2E7D32, the app's green

// A fully collected fylke's fill already sits at RAMP_TO, so the stroke has
// to go darker still to read as a border rather than disappear into the fill.
const COMPLETE_STROKE = "#1B5E20";

export function fylkeColor(playedCount, totalCount) {
	if (!totalCount || !playedCount) return UNPLAYED;

	const share = Math.min(playedCount / totalCount, 1);
	const [r, g, b] = RAMP_FROM.map((from, i) =>
		Math.round(from + (RAMP_TO[i] - from) * share),
	);

	return `rgb(${r}, ${g}, ${b})`;
}

// The card shows the tier name, not the badge label. badgeTitle's labels are
// numeric ("25 klubber"), and printing that beside the real club count puts two
// different numbers on one card — the smaller one reads as a bug.
//
// Bronse is hidden because it is the first few clubs. TIERS already encodes
// where that band ends, so this checks the tier rather than repeating the
// rarity number next to the table that defines it.
export function cardTitle(badges) {
	const best = badgeTitle(badges);
	return best && best.tier.name !== "Bronse" ? best.tier : null;
}

// Southern Norway hugs the west edge, so the bottom-right quadrant is open sea
// from roughly x=420 down — the widest empty region on the card, and the only
// one where a long display name fits at a size worth reading. Measured in a
// browser against the real geometry, not derived from the path data.
const TEXT_RIGHT = VIEWBOX.width - 56;
const TEXT_MAX_WIDTH = 560;

// Inter, because that is what the app actually loads (App.css imports it from
// Google Fonts, and index.js points the MUI theme at it). MUI's own default
// stack is "Roboto","Helvetica","Arial" and Roboto is loaded nowhere in this
// project — asking for it silently yields Helvetica on macOS and Arial on
// Windows, which is the bug commit 554ff59 fixed for the DOM. Canvas text does
// not warn about a fallback, so getting this wrong is invisible until someone
// compares the exported PNG against the page.
const FONT = '"Inter", sans-serif';
// Inter is imported at 300/400/600/800 only — 700 is not one of them and would
// be synthesised.
const BOLD = 800;
const REGULAR = 400;

// Google display names are arbitrary length and the gap beside the coastline is
// fixed, so shrink rather than let a long name run over Rogaland.
function fitText(ctx, text, weight, size) {
	let px = size;
	ctx.font = `${weight} ${px}px ${FONT}`;
	while (px > 24 && ctx.measureText(text).width > TEXT_MAX_WIDTH) {
		px -= 2;
		ctx.font = `${weight} ${px}px ${FONT}`;
	}
}

export function drawCard(
	ctx,
	{ regionStats, name, title, played, total, fylkerComplete, fylkerTotal },
) {
	ctx.fillStyle = "#ffffff";
	ctx.fillRect(0, 0, VIEWBOX.width, VIEWBOX.height);

	for (const [fylke, d] of Object.entries(NORWAY)) {
		const stat = regionStats?.[fylke];
		const played = stat?.playedCount ?? 0;
		const total = stat?.totalCount ?? 0;
		const path = new Path2D(d);

		ctx.fillStyle = fylkeColor(played, total);
		ctx.fill(path);

		// A shade difference between 90% and 100% is not reliably visible at
		// thumbnail size, so a fully collected fylke also gets a darker outline.
		if (total > 0 && played === total) {
			ctx.strokeStyle = COMPLETE_STROKE;
			ctx.lineWidth = 3;
			ctx.stroke(path);
		}
	}

	ctx.textAlign = "right";

	ctx.fillStyle = "#333333";
	fitText(ctx, name ?? "", BOLD, 68);
	ctx.fillText(name ?? "", TEXT_RIGHT, 1108);

	if (title) {
		ctx.fillStyle = title.color;
		fitText(ctx, title.name, BOLD, 44);
		ctx.fillText(title.name, TEXT_RIGHT, 1170);
	}

	const clubs = `${played} av ${total} klubber`;
	const fylker = `${fylkerComplete} av ${fylkerTotal} fylker fullført`;

	ctx.fillStyle = "#2E7D32";
	fitText(ctx, clubs, BOLD, 52);
	ctx.fillText(clubs, TEXT_RIGHT, 1250);

	ctx.fillStyle = "#888888";
	fitText(ctx, fylker, REGULAR, 38);
	ctx.fillText(fylker, TEXT_RIGHT, 1304);
}
